import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../services/api'
import { ChevronLeft, CheckCircle, Clock, RotateCcw, Edit2, Save } from 'lucide-react' // ← Removido o 'X'
import { toast } from 'sonner'

interface Stage {
  id: string
  stage: number
  title: string
  description: string
  status: string
  duration: string
}

export default function StagesManager() {
  const { token } = useAuth()
  const [stages, setStages] = useState<Stage[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState({ title: '', description: '', duration: '' })

  useEffect(() => {
    loadStages()
  }, [token])

  const loadStages = async () => {
    if (!token) {
      console.log('Sem token, não é possível carregar')
      return
    }
    setIsLoading(true)
    try {
      console.log('Carregando fases da API...')
      const data = await api.getAllStages()
      console.log('Fases carregadas:', data)
      setStages(data)
    } catch (error) {
      console.error('Erro ao carregar fases:', error)
      toast.error('Erro ao carregar fases')
    } finally {
      setIsLoading(false)
    }
  }

  const handleStatusChange = async (stageId: string, newStatus: string) => {
    if (!token) return
    setIsSaving(true)
    try {
      console.log(`Atualizando fase ${stageId} para status: ${newStatus}`)
      const updated = await api.updateStage(token, stageId, { status: newStatus })
      console.log('Fase atualizada:', updated)
      toast.success(`Fase ${updated.stage} atualizada com sucesso!`)
      await loadStages()
      
      window.dispatchEvent(new CustomEvent('stages-updated'))
      
    } catch (error) {
      console.error('Erro ao atualizar fase:', error)
      toast.error('Erro ao atualizar fase')
    } finally {
      setIsSaving(false)
    }
  }

  const handleEdit = (stage: Stage) => {
    setEditingId(stage.id)
    setEditForm({
      title: stage.title,
      description: stage.description,
      duration: stage.duration
    })
  }

  const handleSaveEdit = async (stageId: string) => {
    if (!token) return
    
    // Busca a fase atual para obter o status
    const currentStage = stages.find(s => s.id === stageId)
    if (!currentStage) {
      toast.error('Fase não encontrada')
      return
    }
    
    setIsSaving(true)
    try {
      await api.updateStage(token, stageId, {
        status: currentStage.status,  
        title: editForm.title,
        description: editForm.description,
        duration: editForm.duration
      })
      toast.success('Fase atualizada com sucesso!')
      setEditingId(null)
      await loadStages()
      window.dispatchEvent(new CustomEvent('stages-updated'))
    } catch (error) {
      console.error('Erro ao salvar:', error)
      toast.error('Erro ao atualizar fase')
    } finally {
      setIsSaving(false)
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'em-andamento':
        return { bg: 'bg-[#FACC1F]/20 text-[#FACC1F]', icon: <Clock size={12} className="mr-1 animate-pulse" />, label: 'Em andamento' }
      case 'concluido':
        return { bg: 'bg-green-100 text-green-700', icon: <CheckCircle size={12} className="mr-1" />, label: 'Concluído' }
      default:
        return { bg: 'bg-gray-100 text-gray-500', icon: null, label: 'A iniciar' }
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">Carregando fases...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/admin/dashboard" className="text-gray-400 hover:text-gray-600">
              <ChevronLeft size={20} />
            </a>
            <div>
              <h1 className="text-xl font-serif font-bold text-gray-900">Gerenciar Fases do Projeto</h1>
              <p className="text-sm text-gray-500">Altere o status e as datas de cada fase</p>
            </div>
          </div>
          <button
            onClick={loadStages}
            disabled={isLoading}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500 hover:text-[#1D71C3] transition-colors"
          >
            <RotateCcw size={14} className={isLoading ? 'animate-spin' : ''} />
            Recarregar
          </button>
        </div>
      </div>

      <div className="p-6 max-w-4xl mx-auto">
        <div className="relative">
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />
          
          <div className="space-y-8">
            {stages.map((stage) => {
              const badge = getStatusBadge(stage.status)
              const isEmAndamento = stage.status === 'em-andamento'
              const isConcluido = stage.status === 'concluido'
              const isEditing = editingId === stage.id
              
              return (
                <div key={stage.id} className="relative flex gap-5">
                  <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center shrink-0 border-4 border-white shadow-md ${
                    isEmAndamento ? 'bg-[#1D71C3] ring-4 ring-[#1D71C3]/20' :
                    isConcluido ? 'bg-green-500' : 'bg-gray-300'
                  }`}>
                    <span className="text-white font-bold text-sm">{stage.stage}</span>
                  </div>
                  
                  <div className="flex-1 pb-6">
                    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                      {isEditing ? (
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
                            <input
                              type="text"
                              value={editForm.title}
                              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#1D71C3] focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                            <textarea
                              value={editForm.description}
                              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                              rows={2}
                              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#1D71C3] focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Período</label>
                            <input
                              type="text"
                              value={editForm.duration}
                              onChange={(e) => setEditForm({ ...editForm, duration: e.target.value })}
                              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#1D71C3] focus:outline-none"
                              placeholder="Ex: 1º Sem/2026"
                            />
                          </div>
                          <div className="flex justify-end gap-2 pt-2">
                            <button
                              onClick={() => setEditingId(null)}
                              className="px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 rounded-lg"
                            >
                              Cancelar
                            </button>
                            <button
                              onClick={() => handleSaveEdit(stage.id)}
                              disabled={isSaving}
                              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-[#51A64D] text-white rounded-lg hover:bg-[#458f3f]"
                            >
                              <Save size={14} />
                              Salvar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="font-serif font-bold text-lg text-gray-900 mb-1">{stage.title}</h3>
                              <p className="text-sm text-gray-600 mb-3">{stage.description}</p>
                              <div className="flex items-center gap-2 text-xs text-gray-400 mb-4">
                                <Clock size={12} />
                                <span>{stage.duration}</span>
                              </div>
                            </div>
                            <button
                              onClick={() => handleEdit(stage)}
                              className="p-1.5 text-gray-400 hover:text-amber-600 transition-colors"
                              title="Editar título, descrição ou data"
                            >
                              <Edit2 size={16} />
                            </button>
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-gray-100">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg}`}>
                              {badge.icon}
                              {badge.label}
                            </span>
                            
                            <div className="flex gap-2 ml-auto">
                              <button
                                onClick={() => handleStatusChange(stage.id, 'a-iniciar')}
                                disabled={isSaving || stage.status === 'a-iniciar'}
                                className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                                  stage.status === 'a-iniciar'
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                              >
                                Pendente
                              </button>
                              <button
                                onClick={() => handleStatusChange(stage.id, 'em-andamento')}
                                disabled={isSaving || stage.status === 'em-andamento'}
                                className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                                  stage.status === 'em-andamento'
                                    ? 'bg-[#FACC1F]/20 text-[#FACC1F] cursor-not-allowed'
                                    : 'bg-[#FACC1F]/10 text-[#FACC1F] hover:bg-[#FACC1F]/20'
                                }`}
                              >
                                Em andamento
                              </button>
                              <button
                                onClick={() => handleStatusChange(stage.id, 'concluido')}
                                disabled={isSaving || stage.status === 'concluido'}
                                className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                                  stage.status === 'concluido'
                                    ? 'bg-green-100 text-green-500 cursor-not-allowed'
                                    : 'bg-green-50 text-green-600 hover:bg-green-100'
                                }`}
                              >
                                Concluído
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
        
        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-700">
            <strong>ℹ️ Importante:</strong> Ao marcar uma fase como "Em andamento", as demais fases serão automaticamente alteradas para "A iniciar".
            Clique no ícone de lápis <Edit2 size={14} className="inline" /> para editar o título, descrição ou período da fase.
          </p>
        </div>
      </div>
    </div>
  )
}